import { Ticker } from 'pixi.js';
import type { Disposable } from '../core/Event';
import { DefaultRandom, type Random } from '../core/Random';
import { CrashModel } from '../model/crash/CrashModel';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import { DEFAULT_THEME, type Theme } from '../theme';
import { CrashChart } from '../widgets/crash/CrashChart';
import { CrashHudComponent } from './components/CrashHudComponent';
import { SceneBase } from './SceneBase';
import type { SceneLayoutContext } from './SceneComponent';

const STARTING_BALANCE = 500;
const BET_PER_ROUND = 10;

/**
 * Crash demo — Phase F.2 mini-game. Multiplier rises exponentially from
 * 1.00×; tap CASH OUT to lock in your current multiplier as bet × that
 * value. If you wait too long, the round crashes at a pre-rolled point
 * (Pareto distribution; instant 1.00× crash 1% of the time) and the bet
 * is lost.
 *
 * <p>The {@link CrashModel} owns the math; this scene drives the ticker
 * (per-frame {@code model.tick(deltaMs)}) and feeds points to the
 * {@link CrashChart}. Round transitions (idle → rising → cashed-out /
 * crashed → idle) are managed by the model + HUD; the scene just wires
 * the ticker on/off as the status changes.
 */
export class CrashDemoScene extends SceneBase {
  private chart: CrashChart | null = null;
  private hud: CrashHudComponent | null = null;
  private readonly random: Random;
  private readonly model: CrashModel;
  private modelSubscriptions: Disposable[] = [];
  private tickHandler: ((tick: Ticker) => void) | null = null;
  private elapsedThisRound = 0;
  private profileSubscription: Disposable | null = null;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile | null = null,
  ) {
    super(container, { theme });
    this.random = new DefaultRandom();
    this.model = new CrashModel(
      {
        startingBalance: profile?.balance ?? STARTING_BALANCE,
        bet: BET_PER_ROUND,
      },
      this.random,
    );
    if (profile) {
      this.profileSubscription = this.model.events.on('balance-changed', () => {
        profile.setBalance(this.model.balance);
      });
    }
  }

  protected buildContent(): void {
    this.chart = new CrashChart({
      width: 360,
      height: 220,
      curveColor: this.theme.palette.accent,
      cashOutColor: this.theme.palette.warm,
      crashColor: 0xff3b6b,
    });
    this.app.stage.addChild(this.chart);

    this.hud = new CrashHudComponent(this.model, {
      onPlay: () => this.handlePlay(),
      onCashOut: () => this.handleCashOut(),
    });
    this.app.stage.addChild(this.hud);

    this.modelSubscriptions.push(
      this.model.events.on('round-started', () => {
        this.elapsedThisRound = 0;
        this.chart?.reset();
        this.startTicker();
      }),
      this.model.events.on('multiplier-changed', (event) => {
        this.chart?.addPoint(this.elapsedThisRound, event.multiplier);
      }),
      this.model.events.on('cashed-out', (event) => {
        this.chart?.flashCashedOut(event.multiplier);
        this.stopTicker();
      }),
      this.model.events.on('crashed', (event) => {
        // The chart already shows the curve up to crashedAt via the final
        // multiplier-changed event. Flash it red for the post-round freeze.
        this.chart?.flashCrashed(event.crashedAt);
        this.stopTicker();
      }),
    );
  }

  protected onResize(): void {
    this.layout();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.preventDefault();
      if (this.model.status === 'rising') {
        this.handleCashOut();
      } else {
        this.handlePlay();
      }
    }
  }

  protected disposeResources(wasInitialized: boolean): void {
    this.stopTicker();
    this.profileSubscription?.dispose();
    this.profileSubscription = null;
    for (const sub of this.modelSubscriptions) sub.dispose();
    this.modelSubscriptions = [];
    this.model.dispose();
    if (!wasInitialized) return;
    this.hud?.destroy();
    this.hud = null;
    if (this.chart) {
      this.chart.destroy({ children: true });
      this.chart = null;
    }
  }

  private layout(): void {
    if (!this.chart || !this.hud) return;
    const { width, height } = this.app.renderer;
    const centerX = Math.round(width / 2);
    const chartHeight = 220;
    const chartTop = Math.round(height / 2 - chartHeight / 2 - 56 + 16);
    this.chart.x = centerX;
    this.chart.y = chartTop + chartHeight / 2;
    const ctx: SceneLayoutContext = {
      width,
      height,
      centerX,
      machineTop: chartTop,
      machineBottom: chartTop + chartHeight,
      spinButtonY: chartTop + chartHeight + 50,
    };
    this.hud.layout(ctx);
  }

  private handlePlay(): void {
    if (this.model.status === 'rising') return;
    this.model.startRound();
  }

  private handleCashOut(): void {
    if (this.model.status !== 'rising') return;
    this.model.cashOut();
  }

  private startTicker(): void {
    if (this.tickHandler) return;
    this.tickHandler = (tick) => {
      this.elapsedThisRound += tick.deltaMS;
      this.model.tick(tick.deltaMS);
    };
    this.app.ticker.add(this.tickHandler);
  }

  private stopTicker(): void {
    if (!this.tickHandler) return;
    this.app.ticker.remove(this.tickHandler);
    this.tickHandler = null;
  }
}
